import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getBannersList } from "../redux/actions/action-creator";
import { DEFAULT_DELIVERY_LOCATION, LOCATION_STORAGE_KEY } from "../utils/locationDefaults";
import { asObject, getBannerContent, getBannerId, getBannerImage, getCellContents, getValue, normalizeBannerObject } from "../utils/bannerContent";
import { isSuccessResponse } from "../utils/guestLogin";
import ProductImage, { PRODUCT_IMAGE_PLACEHOLDER } from "./common/ProductImage";
import "./market-promo-banner.css";

const EMPTY = {};
const getSavedLocation = () => {
  try { return JSON.parse(localStorage.getItem(LOCATION_STORAGE_KEY)) || EMPTY; }
  catch { return EMPTY; }
};

const getSlides = (data) => getCellContents(data).map((item, index) => {
  const raw = normalizeBannerObject(item);
  const content = getBannerContent(item, index);
  const image = typeof item === "string" ? getBannerImage(item) : content.imageSet.desktop;
  return { raw, content, image, index, id: getBannerId(raw, index) };
}).filter(({ raw, content, image }) =>
  ![false, 0, "0", "false"].includes(raw.is_active) &&
  Boolean(content.title || content.subtitle || content.eyebrow || content.discountText || image !== PRODUCT_IMAGE_PLACEHOLDER)
).sort((left, right) =>
  (Number(left.raw.sort_order) || 0) - (Number(right.raw.sort_order) || 0) || left.index - right.index
);

const themeVariables = (raw) => {
  const theme = asObject(raw.theme || raw.style || raw.styles || raw.colors);
  const background = getValue(theme, ["background", "background_color", "backgroundColor", "bg_color"]) || raw.background_color;
  const colors = {
    "bg-start": theme.background_start_color || background,
    "bg-middle": theme.background_middle_color || background || theme.background_start_color,
    "bg-end": theme.background_end_color || background || theme.background_start_color,
    label: theme.label_color || theme.eyebrow_color || theme.eyebrowColor,
    title: theme.title_color || theme.titleColor,
    subtitle: theme.subtitle_color || theme.subtitleColor,
    "decoration-bottom": theme.decoration_bottom_color,
    "decoration-top": theme.decoration_top_color,
    "decoration-strip": theme.decoration_strip_color,
    "decoration-ring": theme.decoration_ring_color,
    "image-border": theme.image_border_color,
    "image-background": theme.image_background_color,
    "badge-background": theme.badge_background_color || asObject(raw.discount_badge).background,
    "badge-text": theme.badge_text_color || asObject(raw.discount_badge).color,
    "badge-border": theme.badge_border_color,
  };
  const variables = Object.fromEntries(Object.entries(colors)
    .filter(([, value]) => typeof value === "string" && value.trim())
    .map(([key, value]) => [`--market-promo-${key}`, value]));
  if (theme.gradient_angle !== null && theme.gradient_angle !== undefined && Number.isFinite(Number(theme.gradient_angle))) {
    variables["--market-promo-angle"] = `${Number(theme.gradient_angle)}deg`;
  }
  return variables;
};

const PromoImage = ({ slide }) => {
  const [failed, setFailed] = useState(false);
  const { content, image, raw, index } = slide;
  const { mobile, tablet, position } = content.imageSet;
  return <picture>
    {!failed && <>
      {mobile && mobile !== PRODUCT_IMAGE_PLACEHOLDER && <source media="(max-width: 575px)" srcSet={mobile} />}
      {tablet && tablet !== PRODUCT_IMAGE_PLACEHOLDER && <source media="(max-width: 991px)" srcSet={tablet} />}
    </>}
    <ProductImage src={failed ? PRODUCT_IMAGE_PLACEHOLDER : image}
      alt={raw.image_alt || content.mediaAlt || content.title || "Promotion"}
      loading={index === 0 ? "eager" : "lazy"} fetchPriority={index === 0 ? "high" : "auto"}
      style={{ objectPosition: position }} onError={() => setFailed(true)} />
  </picture>;
};

const PromoSlide = ({ slide }) => {
  const { raw, content } = slide;
  const showCopy = Boolean(content.title || content.subtitle || content.eyebrow || content.discountText);
  const showBadge = Boolean(content.discountText);
  const card = showCopy ? <div className={`market-promo__card${showBadge ? "" : " market-promo__card--without-badge"}`} style={themeVariables(raw)}>
    <div className="market-promo__photo"><PromoImage key={slide.image} slide={slide} /></div>
    <div className="market-promo__copy">
      {content.eyebrow && <span>{content.eyebrow}</span>}
      {content.title && <strong>{content.title}</strong>}
      {content.subtitle && <em>{content.subtitle}</em>}
    </div>
    {showBadge && <b className="market-promo__discount">{content.discountText}{raw.badge_label && <small>{raw.badge_label}</small>}</b>}
  </div> : <div className="market-promo__image-card"><PromoImage key={slide.image} slide={slide} /></div>;
  const href = content.href.trim();
  if (/^https?:\/\//i.test(href)) return <a className="market-promo__action" href={href} target="_blank" rel="noreferrer">{card}</a>;
  if (/^\/(?!\/)/.test(href)) return <Link className="market-promo__action" to={href}>{card}</Link>;
  return card;
};

const PromoArrow = ({ direction, onClick, className }) => <button type="button"
  className={`${className || ""} market-promo__arrow market-promo__arrow--${direction}`}
  onClick={onClick} disabled={!onClick} aria-label={direction === "previous" ? "Previous banner" : "Next banner"}>
  {direction === "previous" ? <ChevronLeft size={20} aria-hidden="true" /> : <ChevronRight size={20} aria-hidden="true" />}
</button>;

export default function MarketPromoBanner({ segmentId = "", businessSegmentId = "", categoryId = "", page = "home", enabled = true }) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth || EMPTY);
  const saved = getSavedLocation();
  const location = auth.user_location || EMPTY;
  const area = auth.area ?? location.area ?? location.area_id ?? saved.area ?? saved.area_id;
  const payload = {
    is_dev_stores_enable: "1",
    latitude: String(auth.latitude ?? location.latitude ?? saved.latitude ?? DEFAULT_DELIVERY_LOCATION.latitude),
    longitude: String(auth.longitude ?? location.longitude ?? saved.longitude ?? DEFAULT_DELIVERY_LOCATION.longitude),
    merchant_id: String(location.merchant_id ?? location.merchantId ?? saved.merchant_id ?? saved.merchantId ?? DEFAULT_DELIVERY_LOCATION.merchant_id),
    home_screen: "1", banner_for: "1", segment_id: String(segmentId || ""), calling_from: "main_screen", page,
    ...(area !== undefined && area !== null && area !== "" ? { area: String(area) } : {}),
    ...(businessSegmentId ? { business_segment_id: String(businessSegmentId) } : {}),
    ...(categoryId ? { category_id: String(categoryId) } : {}),
  };
  const requestKey = JSON.stringify({ payload, user: auth.userData?.id || auth.userData?.user_id || "guest" });
  const canLoad = enabled && Boolean(segmentId || businessSegmentId);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!canLoad) return undefined;
    let active = true;
    setResult({ key: requestKey, loading: true, slides: [] });
    const data = new FormData();
    Object.entries(JSON.parse(requestKey).payload).forEach(([name, value]) => data.append(name, value));
    dispatch(getBannersList(data)).then((response) => {
      if (active) setResult({ key: requestKey, slides: isSuccessResponse(response) ? getSlides(response.data) : [] });
    }).catch(() => {
      if (active) setResult({ key: requestKey, slides: [] });
    });
    return () => { active = false; };
  }, [canLoad, dispatch, requestKey]);

  if (!canLoad) return null;
  const loading = result?.key !== requestKey || result?.loading;
  const slides = loading ? [] : result.slides;
  if (!loading && !slides.length) return null;
  const multiple = slides.length > 1;
  return <section className="market-promo" aria-label="Promotional offers" aria-roledescription={multiple ? "carousel" : undefined}>
    <div className="container container-lg market-promo__container">
      {loading ? <div className="market-promo__loading" role="status"><span className="visually-hidden">Loading offers...</span></div> : multiple ?
        <div className="market-promo__carousel">
          <Slider key={`${requestKey}:${slides.map((slide) => slide.id).join(",")}`} dots arrows infinite={false} autoplay={false}
            slidesToShow={1} slidesToScroll={1} speed={450} swipeToSlide accessibility
            prevArrow={<PromoArrow direction="previous" />} nextArrow={<PromoArrow direction="next" />}
            customPaging={(index) => <button type="button" aria-label={`Go to banner ${index + 1}`} />}>
            {slides.map((slide) => <div className="market-promo__slide" key={slide.id}><PromoSlide slide={slide} /></div>)}
          </Slider>
        </div> : <PromoSlide slide={slides[0]} />}
    </div>
  </section>;
}
